<template>
    <div class="page-container">
        <div class="page-header-wrapper" :class="{ 'is-scrolled': isScrolled }">
            <div class="content-wrapper">
                <!-- Page Header -->
                <div class="page-header">
                    <div class="header-left">
                        <h1 class="page-title">{{ page.cardTitle }}</h1>
                        <p class="page-subtitle">Fill in the member's account details and personal information.</p>
                    </div>
                    <div class="header-actions">
                        <button type="button" class="btn-cancel" @click="cancel">Cancel</button>
                        <button type="button" class="btn-save" @click="change()" :disabled="page.isLoading">
                            <span v-if="page.isLoading"><i class="fas fa-spinner fa-spin me-2"></i> Saving...</span>
                            <span v-else><i class="fas fa-check-circle me-2"></i> Save Member</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div class="content-wrapper">
            <div v-if="error" class="alert alert-danger mb-4 shadow-sm" role="alert">
                <i class="fas fa-exclamation-triangle me-2"></i> {{ error }}
            </div>

            <!-- Main Content Grid -->
            <div class="main-grid">
                <!-- Left Column: Main Form -->
                <div class="grid-column main-col content-card">
                    <!-- Section: Identity -->
                    <div class="card-section">
                        <h3 class="section-title">Identity</h3>
                        <p class="section-desc">Basic name and account credentials for this member.</p>

                        <div class="form-grid">
                                <!-- Title + First Name + Last Name -->
                                <div class="form-item form-row">
                                    <div class="form-sub-item" style="flex: 0 0 90px;">
                                        <label>Title <span class="required">*</span></label>
                                        <select v-model="member.title" class="modern-select">
                                            <option disabled value="">-- Select --</option>
                                            <option v-for="option in titles" :key="option.value" :value="option.value">
                                                {{ option.text }}
                                            </option>
                                        </select>
                                        <div v-if="validation.title" class="error-text">{{ validation.title[0] }}</div>
                                    </div>
                                    <div class="form-sub-item" style="flex: 1;">
                                        <label>First Name <span class="required">*</span></label>
                                        <o-input-text name="first_name" :autocomplete="'off'" v-model="member.first_name" type="text" :required="true"/>
                                        <div v-if="validation.first_name" class="error-text">{{ validation.first_name[0] }}</div>
                                    </div>
                                    <div class="form-sub-item" style="flex: 1;">
                                        <label>Last Name <span class="required">*</span></label>
                                        <o-input-text name="last_name" :autocomplete="'off'" v-model="member.last_name" type="text" :required="true"/>
                                        <div v-if="validation.last_name" class="error-text">{{ validation.last_name[0] }}</div>
                                    </div>
                                </div>

                                <!-- Email -->
                                <div v-if="page.cardTitle == 'Create Member'" class="form-item full">
                                    <label>Email <span class="required">*</span></label>
                                    <input type="email" name="fake-email" style="display:none" autocomplete="off" />
                                    <o-input-text name="email" :autocomplete="'new-email'" v-model="member.email" type="email" :required="true" @focus="handleEmailFocus" @blur="handleEmailBlur"/>
                                    <div v-if="validation.email" class="error-text">{{ validation.email[0] }}</div>
                                    <div v-if="emailError" class="error-text">{{ emailError }}</div>
                                </div>

                                <!-- Email + Password (Edit: side by side) -->
                                <div v-if="page.cardTitle == 'Edit Member'" class="form-item form-row">
                                    <div class="form-sub-item">
                                        <label>Email <span class="required">*</span></label>
                                        <input type="email" name="fake-email" style="display:none" autocomplete="off" />
                                        <o-input-text name="email" :autocomplete="'new-email'" v-model="member.email" type="email" :required="true" @focus="handleEmailFocus" @blur="handleEmailBlur"/>
                                        <div v-if="validation.email" class="error-text">{{ validation.email[0] }}</div>
                                        <div v-if="emailError" class="error-text">{{ emailError }}</div>
                                    </div>
                                    <div class="form-sub-item">
                                        <label>Password</label>
                                        <input type="password" name="fake-password" style="display:none" autocomplete="off" />
                                        <div class="password-wrapper">
                                            <o-input-text name="password" :autocomplete="'new-password'" v-model="member.password" :type="showPasswordEdit ? 'text' : 'password'" :required="true" @focus="handlePasswordFocus" @blur="handlePasswordBlur"/>
                                            <button type="button" class="eye-btn" @click="showPasswordEdit = !showPasswordEdit" tabindex="-1">
                                                <i :class="showPasswordEdit ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                                            </button>
                                        </div>
                                        <div v-if="validation.password" class="error-text">{{ validation.password[0] }}</div>
                                        <div v-if="passwordError" class="error-text mt-1">{{ passwordError }}</div>
                                        <small class="form-text text-muted">Leave empty to keep current password.</small>
                                    </div>
                                </div>

                                <!-- Password + Auto Generate (Create only) -->
                                <div v-if="page.cardTitle == 'Create Member'" class="form-item full">
                                    <label>Password</label>
                                    <input type="password" name="fake-password" style="display:none" autocomplete="off" />
                                    <div class="d-flex flex-column flex-sm-row align-items-start gap-3">
                                        <div style="max-width: 300px; width: 100%;">
                                            <div class="password-wrapper">
                                                <o-input-text name="code" :autocomplete="'new-password'" v-model="member.password" :type="showPasswordCreate ? 'text' : 'password'" :required="true" :disabled="autoGenerate" @focus="handlePasswordFocus" @blur="handlePasswordBlur"/>
                                                <button type="button" class="eye-btn" @click="showPasswordCreate = !showPasswordCreate" tabindex="-1">
                                                    <i :class="showPasswordCreate ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                                                </button>
                                            </div>
                                            <div v-if="validation.password" class="error-text">{{ validation.password[0] }}</div>
                                            <div v-if="passwordError" class="error-text mt-1">{{ passwordError }}</div>
                                        </div>
                                        <small class="form-text text-muted" style="flex: 1; font-size: 12px; line-height: 1.5;">
                                            <span class="required">*</span> At least 6 characters with uppercase, lowercase, numbers, and special characters.
                                        </small>
                                    </div>
                                    <div class="d-flex flex-wrap align-items-center gap-3 mt-3" style="padding-left: 2px;">
                                        <button @click="handleAutoGenerate" :disabled="loading" class="generate-button">
                                            <span v-if="loading" class="generating-animation">
                                                Generating
                                                <span class="rect1"></span>
                                                <span class="rect2"></span>
                                                <span class="rect3"></span>
                                            </span>
                                            <span v-else><i class="fas fa-magic me-1"></i> Auto Generate</span>
                                        </button>
                                        <label class="d-flex align-items-center gap-2 mb-0" style="font-size: 13px; color: #475569; cursor: pointer; white-space: nowrap;">
                                            <input type="checkbox" v-model="member.sendEmail" class="toggle-input-sm" style="width: 15px; height: 15px; accent-color: var(--primary-color, #F93E3E); cursor: pointer;" />
                                            <span>Send password to member email</span>
                                        </label>
                                    </div>
                                </div>
                        </div>
                    </div>

                    <hr class="section-divider">

                    <!-- Section: Contact & Address -->
                    <div class="card-section">
                        <h3 class="section-title">Contact & Address</h3>
                        <p class="section-desc">Member's phone number and location details.</p>

                        <div class="form-grid">
                                <!-- Phone + State -->
                                <div class="form-item form-row">
                                    <div class="form-sub-item">
                                        <label>Phone <span class="required">*</span></label>
                                        <div class="d-flex gap-2 align-items-center">
                                            <span v-if="!unknownCountry" class="country-code-badge">{{ countryCode }}</span>
                                            <div style="flex: 1;">
                                                <o-input-text name="phone" :autocomplete="'off'" v-model="member.phone" type="text" :required="true" :numbers-only="true"/>
                                            </div>
                                        </div>
                                        <div v-if="validation.phone" class="error-text">{{ validation.phone[0] }}</div>
                                    </div>
                                    <div class="form-sub-item">
                                        <label>State / Province <span class="required">*</span></label>
                                        <o-input-text name="state" :autocomplete="'off'" v-model="member.state" type="text" :required="true"/>
                                        <div v-if="validation.state" class="error-text">{{ validation.state[0] }}</div>
                                    </div>
                                </div>

                                <!-- City + Post Code -->
                                <div class="form-item form-row">
                                    <div class="form-sub-item">
                                        <label>City / Area <span class="required">*</span></label>
                                        <o-input-text name="city" v-model="member.city" type="text" autocomplete="'off'"></o-input-text>
                                        <div v-if="validation.city" class="error-text">{{ validation.city[0] }}</div>
                                    </div>
                                    <div class="form-sub-item">
                                        <label>Post Code <span class="required">*</span></label>
                                        <o-input-text name="post code" v-model="member.postcode" type="text" autocomplete="'off'"/>
                                        <div v-if="validation.postcode" class="error-text">{{ validation.postcode[0] }}</div>
                                    </div>
                                </div>

                                <!-- Country -->
                                <div class="form-item full">
                                    <label>Country <span class="required">*</span></label>
                                    <v-select
                                        v-model="member.country"
                                        :options="countries"
                                        :reduce="option => option.value"
                                        label="text"
                                        placeholder="-- Search & Select Country --"
                                        class="modern-v-select"
                                        @update:modelValue="updatePhoneCode"
                                    ></v-select>
                                    <div v-if="validation.country" class="error-text mt-1">{{ validation.country[0] }}</div>
                                </div>

                                <!-- Address -->
                                <div class="form-item full">
                                    <label>Address</label>
                                    <o-textarea name="address" v-model="member.address" placeholder="Enter additional address information" style="height: 65px;" for="address" id="address" autocomplete="'off'"></o-textarea>
                                    <div v-if="validation.address" class="error-text">{{ validation.address[0] }}</div>
                                </div>
                        </div>
                    </div>

                    <hr class="section-divider">

                    <!-- Section: Personal & Tier -->
                    <div class="card-section">
                        <h3 class="section-title">Personal & Tier</h3>
                        <p class="section-desc">Optional personal details and membership tier assignment.</p>

                        <div class="form-grid">
                            <!-- Date of Birth & Member ID -->
                            <div class="form-item form-row">
                                <!-- Member ID (Edit only) -->
                                <div v-if="page.cardTitle == 'Edit Member'" class="form-sub-item">
                                    <label>Member ID <span class="text-muted" style="text-transform: capitalize; font-weight: normal; font-size: 11px;">(Read Only)</span></label>
                                    <o-input-text name="alias_id" v-model="member.alias_id" type="text" :disabled="true" :readonly="true"/>
                                </div>
                                <div class="form-sub-item">
                                        <label>Date of Birth</label>
                                        <o-input-text name="date_of_birth" v-model="member.date_of_birth" type="date" :autocomplete="'off'" :max="maxDateOfBirth"/>
                                    </div>
                                </div>

                                <!-- Preference -->
                                <div class="form-item full">
                                    <label>Preference</label>
                                    <o-textarea name="preference" v-model="member.preference" placeholder="Enter member preference" style="height: 65px;" for="preference" id="preference" autocomplete="'off'"></o-textarea>
                                </div>

                                <!-- Lock Tier Toggle Card -->
                                <div class="form-item full">
                                    <label class="lock-tier-card" :class="{ active: member.activateTier }">
                                        <div class="lock-tier-info">
                                            <i class="fas fa-lock lock-icon"></i>
                                            <div>
                                                <div class="lock-tier-title">Lock Tier Member</div>
                                                <div class="lock-tier-desc">Prevent automatic tier upgrades for this member.</div>
                                            </div>
                                        </div>
                                        <div class="lock-tier-toggle">
                                            <input type="checkbox" v-model="member.activateTier" class="toggle-input" />
                                            <div class="toggle-slider"></div>
                                        </div>
                                    </label>
                                </div>

                                <!-- Tier Selector (conditionally shown) -->
                                <div v-if="member.activateTier" class="form-item full mt-2">
                                    <label>Select Member Tier <span class="required">*</span></label>
                                    <div class="tier-cards-container">
                                        <div 
                                            v-for="tier in memberTiers" 
                                            :key="tier.id"
                                            class="tier-card"
                                            :class="{ selected: member.member_tier_id === tier.id }"
                                            @click="member.member_tier_id = tier.id"
                                        >
                                        <div class="tier-image-wrapper">
                                            <img 
                                                v-if="tier.icon && tier.icon.image_url" 
                                                :src="tier.icon.image_url" 
                                                :alt="tier.name" 
                                            />
                                            <img 
                                                v-else-if="tier.image_url" 
                                                :src="tier.image_url" 
                                                :alt="tier.name" 
                                            />
                                            <div v-else class="tier-placeholder">
                                                <i class="fas fa-crown"></i>
                                            </div>
                                        </div>
                                        <div class="tier-name" :title="tier.name">{{ tier.name.length > 10 ? tier.name.substring(0, 10) + '..' : tier.name }}</div>
                                        <div v-if="member.member_tier_id === tier.id" class="tier-check">
                                            <i class="fas fa-check-circle"></i>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="validation.member_tier_id" class="error-text mt-2">{{ validation.member_tier_id[0] }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Right Column: Sidebar -->
                <div class="grid-column side-col">
                    <!-- Avatar Card -->
                    <div class="content-card mb-4">
                        <h3 class="section-title">Avatar <span class="required">*</span></h3>
                        <p class="section-desc">Choose a default or upload a custom one.</p>

                        <div class="default-avatars mb-3">
                            <img v-for="(avatar, index) in defaultAvatars"
                                :key="index"
                                :src="avatar"
                                @click="selectDefaultAvatar(avatar)"
                                :class="{ 'selected-avatar': selectedAvatar === avatar }"
                                class="avatar-option">
                            <div class="or-container">
                                <span class="or-text">or</span>
                            </div>
                        </div>

                        <div class="visual-upload-section">
                            <o-image-input ref="imageInput" name="avatar" id="avatar" :imageurl="member.avatar" @selectImage="imageHandling"></o-image-input>
                        </div>
                        <div v-if="validation.avatar" class="error-text mt-2">{{ validation.avatar[0] }}</div>
                    </div>

                    <!-- Campaign Card -->
                    <div class="content-card">
                        <h3 class="section-title">Set Campaign</h3>
                        <p class="section-desc">Link this member to a specific campaign.</p>

                        <div class="form-item full">
                            <label>Campaign</label>
                            <select v-model="selectedCampaign" class="modern-select">
                                <option disabled value="">-- Select Campaign --</option>
                                <option v-for="option in memberRegisterOptions" :key="option.value" :value="option.value">
                                    {{ option.label }}
                                </option>
                            </select>
                            <div v-if="validation.campaign_id" class="error-text">{{ validation.campaign_id[0] }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Global Loading Overlay -->
        <div v-if="page.isLoading" class="global-loader">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>
    </div>
</template>

<script>
import { OInputText, OImageInput, OTextarea } from '@/components/OmnihControl';
import { reactive, ref, onMounted, onUnmounted, watch, nextTick, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import store from '@/store/'
import { useToast } from '@/composables/useToast'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
    name: 'Addmember',
    components: {
        OInputText,
        OImageInput,
        OTextarea,
        vSelect
    },
    setup() {
        const { showToast } = useToast()
        const loading = ref(false);
        const autoGenerate = ref(false);
        const isScrolled = ref(false);

        const handleScroll = () => {
            isScrolled.value = window.scrollY > 10;
        };

        const page = reactive({
            isLoading: false,
            cardTitle: 'Create Member'
        });

        const member = reactive({
            id: '',
            alias_id: '',

            // for member table
            title: '',
            first_name: '',
            last_name: '',
            user_id: '',
            corporate_id: '',
            
            // for user table
            email: '',
            password: '',
            
            // for user profile table
            avatar: '',
            phone: '',
            address: '',
            state: '',
            date_of_birth: '',
            preference: '',

            image_url: '',
            country: 101,
            city: '',
            postcode: '',
            sendEmail: false,

            member_tier_id:'',
            activateTier:''
        });
        const titles = ref([
            { value: 'Mr.', text: 'Mr.' },
            { value: 'Mrs.', text: 'Mrs.' },
            { value: 'Miss', text: 'Miss' }
        ]);
        const error = ref(null);
        const emailError = ref(null);
        const passwordError = ref(null);
        const options = ref([]);
        const validation = ref([]);
        const router = useRouter();
        const route = useRoute();
        const corporates = ref([]);
        const countryCode = ref('');
        const unknownCountry = ref(false);
        const countries = ref([]);
        const countriesData = ref([]);

        const imageInput = ref(null);

        const defaultAvatars = [
            'https://s3.ap-southeast-1.amazonaws.com/cdn-member.omnihotelier.com/images/woman.png',
            'https://s3.ap-southeast-1.amazonaws.com/cdn-member.omnihotelier.com/images/man.png',
        ];

        const selectedAvatar = ref(null);

        const showUpload = ref(false);
        const showPasswordEdit = ref(false);
        const showPasswordCreate = ref(false);

        const memberRegisterOptions = ref([]);
        const selectedCampaign = ref(null);
        

        onMounted(async () => {
            // Ensure page is at the top when opened
            window.scrollTo(0, 0);
            window.addEventListener('scroll', handleScroll);
            
            page.isLoading = true;

            await loadCountries();
            if (route.params.id) {
                await loadState(route.params.id);
            }
            updatePhoneCode();

            try {
                const response = await store.dispatch('Auth/getBusinessProfile');

                if (response) {
                    let entity_id = null;

                    if (response.corporate_id && !response.branch_id && !response.merchant_id) {
                        entity_id = response.corporate_id;
                    } else if (response.corporate_id && response.branch_id && !response.merchant_id) {
                        entity_id = response.branch_id;
                    } else if (response.corporate_id && response.branch_id && response.merchant_id) {
                        entity_id = response.merchant_id;
                    }

                    member.entity_id = entity_id;
                }

                const campaignResponse = await store.dispatch('Member/read_member_register');
                if (Array.isArray(campaignResponse.data)) {
                    memberRegisterOptions.value = campaignResponse.data.map(item => ({
                        value: item.id,
                        label: item.name || item.title || `Campaign ${item.id}`
                    }));
                } else {
                    console.error('Expected an array inside data, but received:', campaignResponse.data);
                    memberRegisterOptions.value = [];
                }
                page.isLoading = false;
            } catch (e) {
                console.error('Error fetching member register data:', e);
                memberRegisterOptions.value = [];
                page.isLoading = false;
            }
        });

        onUnmounted(() => {
            window.removeEventListener('scroll', handleScroll);
        });

        async function loadCountries() {
            try {
                const response = await store.dispatch('Countries/read');
                countriesData.value = response; // Store full country data
                countries.value = response.map(country => ({ 
                    value: country.id, 
                    text: `${country.name} (${country.phone_code ? country.phone_code : '-'})` 
                }));
                options.value = countries.value;
                if (countries.value.length > 0) {
                    loadCorporate();
                }
            } catch (err) {
                console.error(err);
            }
        }

        function loadCorporate() {
            store.dispatch('Auth/getBusinessProfile').then(businessProfile => {
                member.corporate_id = businessProfile.corporate_id;
                store.dispatch('MemberTier/read_all', businessProfile.corporate_id);
            });
        }

        async function loadState(id) {
            try {
                const data = await store.dispatch('Member/read_single', id);
                Object.assign(member, {
                    country: Number(data.user.user_profile.country) || 101,
                    id: data.id,
                    alias_id: data.alias_id,
                    title: data.title,

                    first_name: data.first_name,
                    last_name: data.last_name,
                    user_id: data.user_id,
                    corporate_id: data.corporate_id,
                    email: data.user.email,
                    level_ids: data.user.levels.at(0).id,
                    address: data.user.user_profile.address,
                    state: data.user.user_profile.state,
                    avatar: data.user?.user_profile?.avatar?.image_url,
                    phone: data.user.user_profile.phone,
                    city: data.user.user_profile.city,
                    postcode: data.user.user_profile.postcode,
                    date_of_birth: data.user.user_profile.date_of_birth ? data.user.user_profile.date_of_birth.substring(0, 10) : '',
                    preference: data.user.user_profile.preference,
                    member_tier_id: data.member_tiering?.tier_id,
                    activateTier: data.not_upgrade === 1
                });

                // Remove country code from phone after country is set
                member.phone = removeCountryCode(member.phone);

                page.cardTitle = 'Edit Member';

                if (data.member_register_trackers && data.member_register_trackers.length > 0 && data.member_register_trackers[0].campaign_id) {
                    const campaignId = data.member_register_trackers[0].campaign_id;
                    selectedCampaign.value = campaignId;
                }
            } catch (err) {
                console.error(err);
            }
        }

        function imageHandling(img) {
            member.avatar = img;
            selectedAvatar.value = img;
        }

        function selectDefaultAvatar(img) {
            member.avatar = img;
            selectedAvatar.value = img;
            imageInput.value.clear();
        }

        function toggleUpload() {
            showUpload.value = !showUpload.value;
        }

        const handleAutoGenerate = async () => {
            loading.value = true;
            await new Promise((resolve) => setTimeout(resolve, 2000)); // Simulasi waktu loading
            member.password = generateRandomPassword();
            loading.value = false;
        };

        const generateRandomPassword = () => {
            const upperCaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
            const lowerCaseChars = 'abcdefghijklmnopqrstuvwxyz';
            const numberChars = '0123456789';
            const specialChars = '!@#$%^&*()_+[]{}|;:,.<>?';

            const allChars = upperCaseChars + lowerCaseChars + numberChars + specialChars;
            let password = '';

            password += upperCaseChars.charAt(Math.floor(Math.random() * upperCaseChars.length));
            password += lowerCaseChars.charAt(Math.floor(Math.random() * lowerCaseChars.length));
            password += numberChars.charAt(Math.floor(Math.random() * numberChars.length));
            password += specialChars.charAt(Math.floor(Math.random() * specialChars.length));

            for (let i = password.length; i < 12; i++) {
                password += allChars.charAt(Math.floor(Math.random() * allChars.length));
            }

            password = password.split('').sort(() => 0.5 - Math.random()).join('');

            return password;
        };

        async function change() {
            page.isLoading = true;

            validation.value = {}; // Reset validation errors to an object
            let hasFEError = false;

            if (!member.title) { validation.value.title = ["The title field is required."]; hasFEError = true; }
            if (!member.first_name) { validation.value.first_name = ["The first name field is required."]; hasFEError = true; }
            if (!member.last_name) { validation.value.last_name = ["The last name field is required."]; hasFEError = true; }
            if (!member.email) { validation.value.email = ["The email field is required."]; hasFEError = true; }
            
            if (page.cardTitle === 'Create Member' && !autoGenerate.value && !member.password) {
                validation.value.password = ["The password field is required."];
                hasFEError = true;
            }

            if (!member.phone) {
                validation.value.phone = ["The phone field is required."];
                hasFEError = true;
            } else if (member.phone.length < 4) {
                validation.value.phone = ["The phone must be at least 4 digits."];
                hasFEError = true;
            }

            if (!member.state) { validation.value.state = ["The state field is required."]; hasFEError = true; }
            if (!member.city) { validation.value.city = ["The city field is required."]; hasFEError = true; }
            if (!member.postcode) { validation.value.postcode = ["The post code field is required."]; hasFEError = true; }
            if (!member.country) { validation.value.country = ["The country field is required."]; hasFEError = true; }
            if (!member.avatar) { validation.value.avatar = ["The avatar field is required."]; hasFEError = true; }
            if (member.activateTier && !member.member_tier_id) { validation.value.member_tier_id = ["The member tier field is required."]; hasFEError = true; }

            if (hasFEError) {
                page.isLoading = false;
                nextTick(() => {
                    const firstError = document.querySelector('.error-text');
                    if (firstError) {
                        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    } else {
                        scrollToTop();
                    }
                });
                return;
            }

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (member.email && !emailRegex.test(member.email)) {
                emailError.value = "Please enter a valid email address.";
                page.isLoading = false;
                scrollToTop();
                return;
            } else {
                emailError.value = null;
            }

            const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{6,16}$/;
            if (member.password && !passwordRegex.test(member.password)) {
                passwordError.value = "Minimum 6 characters with uppercase, lowercase, numbers, and special characters.";
                page.isLoading = false;
                scrollToTop();
                return;
            } else {
                passwordError.value = null;
            }

            member.phone = (countryCode.value + member.phone).replace(/\s+/g, '');
            const data = { 
                ...member,
                campaign_id: selectedCampaign.value
            };

            try {
                if (route.name !== 'MemberForm') {
                    if (!data.avatar) delete data.avatar;
                    await store.dispatch("Member/update", { data });
                    showToast({
                        type: 'success',
                        title: 'Updated',
                        message: `Member has been updated successfully.`
                    })
                } else {
                    if (autoGenerate.value) {
                        data.sendEmail = true;
                        data.password = await generateRandomPassword();
                    }
                    await store.dispatch('Member/create', { data });
                    showToast({
                        type: 'success',
                        title: 'Created',
                        message: `Member has been created successfully.`
                    })
                }
                
                // Redirect back to member list
                if (route.name !== 'MemberForm' && route.query.page) {
                    // Redirect back to the same page after edit
                    router.push({ name: 'Member', query: { page: route.query.page } });
                } else {
                    // Redirect to Member list at last page for new member
                    router.push({ name: 'Member', query: { newMember: 'true' } });
                }
            } catch (err) {
                member.phone = removeCountryCode(member.phone);
                handleError(err);
            } finally {
                page.isLoading = false;
            }
        }

        function scrollToTop() {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }

        function handleError(err) {
            if (err.response && err.response.status === 422) {
                validation.value = err.response.data.errors;
            } else if (err.response && err.response.status === 400) {
                if (err.response.data.error && err.response.data.error.includes("PDOException: SQLSTATE[23000]")) {
                    error.value = "Email is already in use by another user. Please use a different email.";
                } else if (err.response.data.error) {
                    try {
                        validation.value = JSON.parse(err.response.data.error);
                    } catch(e) {
                        error.value = err.response.data.message || err.response.data.error;
                    }
                }
            } else if (err.response && err.response.status === 403) {
                error.value = err.response.data.message;
            }
            // Scroll to first error field after DOM updates
            nextTick(() => {
                const firstError = document.querySelector('.error-text');
                if (firstError) {
                    firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else {
                    scrollToTop();
                }
            });
        }

        function updatePhoneCode() {
            if (!member.country) {
                unknownCountry.value = true;
                return;
            }

            const country = countriesData.value.find(c => c.id == member.country);

            if (country && country.phone_code) {
                unknownCountry.value = false;
                countryCode.value = `${country.phone_code} `;
            } else {
                unknownCountry.value = true;
                console.error(`Phone code not found for country ID: ${member.country}`);
            }
        }

        function removeCountryCode(phone) {
            if (!member.country || !phone) {
                return phone;
            }

            const country = countriesData.value.find(c => c.id == member.country);

            if (country && country.phone_code) {
                const escapedPhoneCode = country.phone_code.replace('+', '\\+');
                const phoneCodePattern = new RegExp(`^${escapedPhoneCode}`);
                return phone.replace(phoneCodePattern, '').trim();
            }
            return phone;
        }

        function cancel() {
            router.push({ name: 'Member' });
        }

        watch(() => member.country, updatePhoneCode);

        // --- AUTOCOMPLETE HANDLERS ---
        const handleEmailFocus = (event) => {
            event.target.setAttribute('autocomplete', 'email');
        };
        const handleEmailBlur = (event) => {
            event.target.setAttribute('autocomplete', 'new-email');
        };
        const handlePasswordFocus = (event) => {
            event.target.setAttribute('autocomplete', 'current-password');
        };
        const handlePasswordBlur = (event) => {
            event.target.setAttribute('autocomplete', 'new-password');
        };

        const validateEmail = (value) => {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (value && !emailRegex.test(value)) {
                emailError.value = "Please enter a valid email address.";
            } else {
                emailError.value = null;
            }
        };

        watch(() => member.email, (newVal) => {
            if(newVal) validateEmail(newVal);
            else emailError.value = null;
        });

        const validatePassword = (value) => {
            const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{6,16}$/;
            if (value && !passwordRegex.test(value)) {
                passwordError.value = "Minimum 6 characters with uppercase, lowercase, numbers, and special characters.";
            } else {
                passwordError.value = null;
            }
        };

        watch(() => member.password, (newVal) => {
            if (newVal) validatePassword(newVal);
            else passwordError.value = null;
        });

        const memberTiers = computed(() => store.state.MemberTier.allTiers || []);

        const maxDateOfBirth = computed(() => {
            const today = new Date();
            const year = today.getFullYear();
            let month = String(today.getMonth() + 1).padStart(2, '0');
            let day = String(today.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        });

        return {
            isScrolled,
            member,
            memberTiers,
            maxDateOfBirth,
            validation,
            router,
            options,
            route,
            change,
            page,
            imageHandling,
            corporates,
            error,
            titles,
            countryCode,
            unknownCountry,
            countries,
            cancel,
            showUpload,
            showPasswordEdit,
            showPasswordCreate,
            selectedAvatar,
            imageInput,
            defaultAvatars,
            selectDefaultAvatar,
            toggleUpload,
            removeCountryCode,
            autoGenerate,
            handleAutoGenerate,
            loading,
            handleEmailFocus,
            handleEmailBlur,
            handlePasswordFocus,
            handlePasswordBlur,
            validateEmail,
            emailError,
            passwordError,
            validatePassword,
            memberRegisterOptions,
            selectedCampaign,
            showToast
        };
    }
};
</script>

<style scoped>
/* Page Layout */
.page-container {
    width: 100%;
    min-height: calc(100vh - 64px);
    background-color: #f1f4f9;
    padding: 24px 40px;
}

.content-wrapper {
    max-width: 1240px;
    margin: 0 auto;
}

/* Page Header Wrapper */
.page-header-wrapper {
    position: sticky;
    top: 0;
    z-index: 99;
    margin: -24px -40px 16px -40px;
    padding: 24px 40px 0 40px;
    background: rgba(241, 244, 249, 0);
    border-bottom: 1px solid rgba(0, 0, 0, 0);
    box-shadow: 0 10px 20px 5px rgba(241, 244, 249, 0);
    backdrop-filter: blur(0px);
    -webkit-backdrop-filter: blur(0px);
    transition: background 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease, backdrop-filter 0.3s ease, -webkit-backdrop-filter 0.3s ease;
}

.page-header-wrapper.is-scrolled {
    /* Glassmorphism Effect */
    background: rgba(241, 244, 249, 0.85);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border-bottom: 1px solid rgba(0, 0, 0, 0.03);
    box-shadow: 0 10px 20px 5px rgba(241, 244, 249, 0.5);
}

/* Page Header */
.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
    padding-bottom: 16px;
}

.page-title {
    font-size: 26px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.5px;
    margin: 0;
}

.page-subtitle {
    color: #64748b;
    font-size: 14px;
    margin: 4px 0 0 0;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
}

/* Buttons */
.btn-cancel {
    padding: 10px 24px;
    border: 1px solid #e2e8f0;
    background: white;
    color: #475569;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s;
}

.btn-cancel:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
}

.btn-save {
    padding: 10px 24px;
    background: var(--primary-color, #F93E3E);
    border: none;
    color: white;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.3s ease, box-shadow 0.3s ease, filter 0.3s ease;
    display: flex;
    align-items: center;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.btn-save:hover {
    filter: brightness(0.9);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.15);
}

.btn-save:disabled {
    background: #cbd5e1;
    box-shadow: none;
    cursor: not-allowed;
}

/* Grid Layout */
.main-grid {
    display: grid;
    grid-template-columns: 1fr 360px;
    gap: 24px;
    align-items: start;
}

/* Content Cards */
.content-card {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    padding: 28px 32px;
    border: 1px solid rgba(0, 0, 0, 0.05);
}

.section-title {
    font-size: 17px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 4px;
}

.section-desc {
    font-size: 13px;
    color: #64748b;
    margin-bottom: 20px;
}

.section-divider {
    border: none;
    height: 2px;
    background: linear-gradient(70deg, transparent, #64748b, transparent);
    margin: 28px 2px;
}

/* Form Styling */
.form-grid {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.form-item label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.form-item.full {
    width: 100%;
}

.form-item.half {
    width: 50%;
}

/* A row of two or more side-by-side sub-items */
.form-item.form-row {
    display: flex;
    gap: 16px;
}

.form-sub-item {
    flex: 1;
    min-width: 0;
}

.form-sub-item label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.required {
    color: #ef4444;
}

.error-text {
    color: #ef4444;
    font-size: 12px;
    margin-top: 5px;
    font-weight: 500;
}

.modern-select {
    width: 100%;
    padding: 11px 14px !important;
    border: 1.5px solid #e2e8f0 !important;
    border-radius: 8px !important;
    font-size: 14px !important;
    color: #0f172a !important;
    background-color: #f8fafc !important;
    margin: 0 !important;
    transition: all 0.2s;
    appearance: auto;
}

/* Ensure no wrapper margins break Flex alignment */
:deep(.input-wrapper) {
    margin: 0 !important;
}


.modern-select:focus {
    border-color: var(--primary-color, #F93E3E) !important;
    background-color: #ffffff !important;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.06) !important;
    outline: none !important;
}

/* Override o-input-text & o-select focus color to match BenefitForm */
:deep(input),
:deep(textarea) {
    width: 100%;
    padding: 11px 14px !important;
    border: 1.5px solid #e2e8f0 !important;
    border-radius: 8px !important;
    font-size: 14px !important;
    color: #0f172a !important;
    background-color: #f8fafc !important;
    transition: all 0.2s;
}

:deep(input:focus),
:deep(textarea:focus) {
    border-color: var(--primary-color, #F93E3E) !important;
    background-color: #ffffff !important;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.06) !important;
    outline: none !important;
}

:deep(input:disabled),
:deep(input[readonly]) {
    background-color: #f1f5f9 !important;
    color: #94a3b8 !important;
    cursor: not-allowed;
}

:deep(.vs__dropdown-toggle) {
    padding: 6px 10px !important;
    border: 1.5px solid #e2e8f0 !important;
    border-radius: 8px !important;
    background-color: #f8fafc !important;
    transition: all 0.2s;
}

:deep(.vs__dropdown-toggle:focus-within),
:deep(.vs--open .vs__dropdown-toggle) {
    border-color: var(--primary-color, #F93E3E) !important;
    background-color: #ffffff !important;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.06) !important;
}

/* Reset generic input overrides inside vue-select */
:deep(.vs__search),
:deep(.vs__search:focus) {
    width: auto !important;
    flex: 1 !important;
    padding: 4px 6px !important;
    border: none !important;
    border-radius: 0 !important;
    background-color: transparent !important;
    box-shadow: none !important;
    font-size: 14px !important;
    color: #0f172a !important;
    margin: 0 !important;
    outline: none !important;
}

/* Password Wrapper & Eye Toggle */
.password-wrapper {
    position: relative;
    display: block;
    width: 100%;
}

.password-wrapper :deep(input) {
    padding-right: 42px !important;
    width: 100%;
}

.eye-btn {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    padding: 4px;
    color: #94a3b8;
    cursor: pointer;
    font-size: 14px;
    line-height: 1;
    transition: color 0.2s;
    z-index: 2;
}

.eye-btn:hover {
    color: #475569;
}

/* Country Code Badge */
.country-code-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: var(--primary-color, #F7A3A7);
    color: #ffffff;
    font-size: 13px;
    font-weight: 600;
    padding: 0 12px;
    height: 44px;
    border-radius: 8px;
    white-space: nowrap;
    letter-spacing: 0.5px;
    flex-shrink: 0;
}

/* Lock Tier Toggle Card */
.lock-tier-card {
    display: flex !important;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 20px;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    background-color: #f8fafc;
    cursor: pointer;
    transition: all 0.2s;
    user-select: none;
    width: 100%;
}

.lock-tier-card:hover {
    border-color: #cbd5e1;
    background-color: #f1f5f9;
}

.lock-tier-card.active {
    border-color: var(--primary-color, #F93E3E);
    background-color: var(--secondary-color, #FEE9E9);
}

.lock-tier-info {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-grow: 1;
}

.lock-icon {
    font-size: 18px;
    color: #94a3b8;
    transition: color 0.2s;
    flex-shrink: 0;
}

.lock-tier-card.active .lock-icon {
    color: var(--primary-color, #F93E3E);
}

.lock-tier-title {
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
}

.lock-tier-desc {
    font-size: 12px;
    color: #64748b;
    margin-top: 2px;
    text-transform: none;
    letter-spacing: 0;
    font-weight: 400;
}

/* Toggle Switch */
.lock-tier-toggle {
    position: relative;
    width: 44px;
    height: 24px;
    flex-shrink: 0;
}

.toggle-input {
    opacity: 0 !important;
    width: 0 !important;
    height: 0 !important;
    position: absolute;
    padding: 0 !important;
    margin: 0 !important;
    border: none !important;
    box-shadow: none !important;
    background: none !important;
}

.toggle-slider {
    position: absolute;
    inset: 0;
    background: #cbd5e1;
    border-radius: 99px;
    transition: background 0.2s;
    cursor: pointer;
}

.toggle-slider::before {
    content: '';
    position: absolute;
    width: 18px;
    height: 18px;
    left: 3px;
    top: 3px;
    background: white;
    border-radius: 50%;
    transition: transform 0.2s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.toggle-input:checked + .toggle-slider {
    background: var(--primary-color, #F93E3E);
}

.toggle-input:checked + .toggle-slider::before {
    transform: translateX(20px);
}

/* Password Tools */
.password-tools {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.generate-button {
    padding: 6px 14px;
    background-color: var(--primary-color, #9bd8b5);
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    display: flex;
    align-items: center;
    transition: background 0.2s;
}

.generate-button:hover {
    background-color: var(--secondary-color, #7cc9a0);
}

.generate-button:disabled {
    background-color: #c2e5d3;
    cursor: not-allowed;
}

.generating-animation {
    display: flex;
    align-items: center;
    gap: 2px;
}

.generating-animation span {
    display: inline-block;
    width: 4px;
    height: 10px;
    background-color: white;
    animation: loading 1.2s infinite ease-in-out;
}

.generating-animation .rect1 { animation-delay: -1.2s; }
.generating-animation .rect2 { animation-delay: -1.1s; }
.generating-animation .rect3 { animation-delay: -1.0s; }

@keyframes loading {
    0%, 40%, 100% { transform: scaleY(0.4); }
    20% { transform: scaleY(1); }
}

/* Avatar Section */
.default-avatars {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.avatar-option {
    width: 52px;
    height: 52px;
    cursor: pointer;
    border-radius: 50%;
    border: 2px solid transparent;
    transition: border-color 0.2s;
}

.avatar-option:hover {
    border-color: var(--primary-color, #0d6efd);
}

.selected-avatar {
    outline: 2px solid var(--primary-color, #0d6efd);
    border-radius: 50%;
}

.or-container {
    display: flex;
    align-items: center;
}

.or-text {
    font-weight: 700;
    color: #94a3b8;
    font-size: 13px;
}

.visual-upload-section {
    background: white;
}

/* Global Loader */
.global-loader {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(2px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

/* Responsive */
@media (max-width: 1024px) {
    .main-grid {
        grid-template-columns: 1fr;
    }
    .form-item.half {
        width: 100%;
    }
}

@media (max-width: 640px) {
    .page-container {
        padding: 12px 8px;
    }
    .page-header-wrapper {
        margin: -12px -8px 16px -8px;
        padding: 12px 8px 0 8px;
    }
    .page-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
        padding-bottom: 12px;
    }
    .header-left {
        width: 100%;
        padding-right: 48px; /* Beri ruang agar teks judul tidak tertutup avatar */
    }
    .page-title {
        font-size: 20px;
        margin: 0;
    }
    .page-subtitle {
        display: none; /* Sembunyikan subtitle di mobile agar header tidak terlalu tinggi */
    }
    .header-actions {
        width: 100%;
        gap: 8px;
        justify-content: flex-start;
    }
    .btn-cancel, .btn-save {
        padding: 8px 16px;
        font-size: 13px;
    }
    .form-item.form-row {
        flex-direction: column;
    }
    .content-card {
        padding: 16px 12px;
    }
}

/* Tier Selection Cards */
.tier-cards-container {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
}

@media (max-width: 1024px) {
    .tier-cards-container {
        grid-template-columns: repeat(3, 1fr);
    }
}

@media (max-width: 640px) {
    .tier-cards-container {
        grid-template-columns: repeat(2, 1fr);
    }
}

.tier-card {
    position: relative;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    padding: 16px;
    cursor: pointer;
    background: white;
    display: flex;
    flex-direction: column;
    align-items: center;
    transition: all 0.2s ease;
}

.tier-card:hover {
    border-color: #cbd5e1;
    background: #f8fafc;
}

.tier-card.selected {
    border-color: var(--primary-color, #fc6666);
    background: var(--secondary-color, #FEE9E9);
}

.tier-image-wrapper {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    margin-bottom: 12px;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.tier-image-wrapper img {
    width: 60%;
    height: 60%;
    object-fit: contain;
}

.tier-placeholder {
    color: #94a3b8;
    font-size: 24px;
}

.tier-card.selected .tier-placeholder {
    color: var(--primary-color, #fc6666);
}

.tier-name {
    font-size: 14px;
    font-weight: 600;
    color: #334155;
    text-align: center;
}

.tier-card.selected .tier-name {
    color: var(--primary-color, #fc6666);
}

.tier-check {
    position: absolute;
    top: -8px;
    right: -8px;
    color: var(--primary-color, #fc6666);
    font-size: 20px;
    background: white;
    border-radius: 50%;
    line-height: 1;
}
/* Vue-Select Modern Overrides */
:deep(.modern-v-select) {
    --vs-border-color: #cbd5e1;
    --vs-border-radius: 8px;
    --vs-font-size: 14px;
    --vs-colors--light: #64748b;
}

:deep(.modern-v-select .vs__dropdown-toggle) {
    background-color: #f8fafc;
    border: 1px solid var(--vs-border-color);
    border-radius: var(--vs-border-radius);
    padding: 6px 4px;
    min-height: 42px;
    margin: 0 !important;
    transition: all 0.2s ease;
}

:deep(.modern-v-select.vs--open .vs__dropdown-toggle),
:deep(.modern-v-select .vs__dropdown-toggle:focus-within) {
    background-color: #ffffff;
    border-color: var(--primary-color, #F93E3E);
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.06);
}

:deep(.modern-v-select .vs__search::placeholder),
:deep(.modern-v-select .vs__dropdown-toggle),
:deep(.modern-v-select .vs__dropdown-menu) {
    font-family: inherit;
    font-size: 14px;
    color: #334155;
}

:deep(.modern-v-select .vs__clear),
:deep(.modern-v-select .vs__open-indicator) {
    fill: #94a3b8;
}

:deep(.modern-v-select .vs__dropdown-menu) {
    border-color: #e2e8f0;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border-radius: 8px;
    margin-top: 4px;
    padding: 4px 0;
}

:deep(.modern-v-select .vs__dropdown-option) {
    padding: 8px 16px;
    color: #334155;
}

:deep(.modern-v-select .vs__dropdown-option--highlight) {
    background: var(--primary-color, #F93E3E);
    color: #ffffff;
}
</style>